import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./buyhouse.css";
import {
  ArrowLeft,
  Phone,
  MessageCircle,
  Mail,
  Search,
  MapPin,
  Heart,
  SlidersHorizontal,
  RotateCcw,
  Grid2X2,
  List,
  ChevronDown,
  ShieldCheck,
  Users,
  BadgeCheck,
  X,
  ArrowUpRight,
} from "lucide-react";

function BuyHouse() {
  const navigate = useNavigate();

  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showConsult, setShowConsult] = useState(false);
  const [consultUser, setConsultUser] = useState(null);

  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("latest");

  const [viewMode, setViewMode] = useState("grid");
  const [favorites, setFavorites] = useState([]);
  const [activePrice, setActivePrice] = useState("all");

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("civilink_user"));
    } catch {
      return null;
    }
  }, []);

  /* =========================================
     FETCH HOUSES
  ========================================= */

  const fetchHouses = async (customFilters = {}) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      const currentSearch =
        customFilters.search !== undefined
          ? customFilters.search
          : search;

      const currentLocation =
        customFilters.location !== undefined
          ? customFilters.location
          : location;

      const currentMinPrice =
        customFilters.minPrice !== undefined
          ? customFilters.minPrice
          : minPrice;

      const currentMaxPrice =
        customFilters.maxPrice !== undefined
          ? customFilters.maxPrice
          : maxPrice;

      const currentSort =
        customFilters.sort !== undefined
          ? customFilters.sort
          : sort;

      if (currentSearch) params.append("q", currentSearch);
      if (currentLocation) params.append("location", currentLocation);
      if (currentMinPrice) params.append("minPrice", currentMinPrice);
      if (currentMaxPrice) params.append("maxPrice", currentMaxPrice);
      if (currentSort) params.append("sort", currentSort);

      const res = await fetch(`/api/house/buy?${params.toString()}`);

      if (!res.ok) {
        throw new Error("Failed to fetch houses");
      }

      const data = await res.json();

      if (data.success) {
        setHouses(data.houses || []);
      } else {
        setHouses([]);
      }
    } catch (error) {
      console.error("Buy house fetch error:", error);
      setHouses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHouses();
  }, []);

  /* =========================================
     CONTACT SELLER
  ========================================= */

  const contactSeller = (seller) => {
    if (!localStorage.getItem("token")) {
      navigate("/");
      return;
    }

    setConsultUser(seller);
    setShowConsult(true);
  };

  /* =========================================
     FAVORITES
  ========================================= */

  const toggleFavorite = (houseId) => {
    setFavorites((prev) =>
      prev.includes(houseId)
        ? prev.filter((id) => id !== houseId)
        : [...prev, houseId]
    );
  };

  /* =========================================
     QUICK PRICE FILTER
  ========================================= */

  const applyPriceChip = (type) => {
    setActivePrice(type);

    let newMin = "";
    let newMax = "";

    if (type === "under30") {
      newMax = "3000000";
    }

    if (type === "30to60") {
      newMin = "3000000";
      newMax = "6000000";
    }

    if (type === "60to1cr") {
      newMin = "6000000";
      newMax = "10000000";
    }

    if (type === "1crplus") {
      newMin = "10000000";
    }

    setMinPrice(newMin);
    setMaxPrice(newMax);

    fetchHouses({
      minPrice: newMin,
      maxPrice: newMax,
    });
  };

  /* =========================================
     RESET
  ========================================= */

  const resetFilters = () => {
    setSearch("");
    setLocation("");
    setMinPrice("");
    setMaxPrice("");
    setSort("latest");
    setActivePrice("all");

    fetchHouses({
      search: "",
      location: "",
      minPrice: "",
      maxPrice: "",
      sort: "latest",
    });
  };

  /* =========================================
     FORMAT PRICE
  ========================================= */

  const formatPrice = (price) => {
    const value = Number(price);

    if (!value) return "Price on request";

    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    }

    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }

    return `₹${value.toLocaleString("en-IN")}`;
  };

  const formatFullPrice = (price) => {
    const value = Number(price);

    if (!value) return "";

    return `₹${value.toLocaleString("en-IN")}`;
  };

  /* =========================================
     UI
  ========================================= */

  return (
    <div className="buyhouse-page">

      <div className="buyhouse-back-wrapper">
  <button
    className="buyhouse-back-btn"
    onClick={() => navigate(-1)}
    aria-label="Go back"
  >
    <ArrowLeft size={18} />
    <span>Back</span>
  </button>
</div>

      {/* =====================================
          HERO
      ===================================== */}

      <section className="buyhouse-hero">

        <div className="hero-overlay" />

        <div className="hero-content">

          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Trusted property marketplace
          </div>

          <h1>
            Find a place
            <br />
            <span>you'll love to call home.</span>
          </h1>

          <p>
            Explore houses from trusted sellers and find
            the right property for your next chapter.
          </p>

          <div className="hero-trust-row">

            <div>
              <ShieldCheck size={17} />
              Verified Listings
            </div>

            <div>
              <Users size={17} />
              Trusted Sellers
            </div>

            <div>
              <BadgeCheck size={17} />
              Easy Communication
            </div>

          </div>

        </div>

      </section>

      {/* =====================================
          SEARCH PANEL
      ===================================== */}

      <section className="buyhouse-search-wrapper">

        <div className="buyhouse-search-card">

          <div className="search-field search-main">

            <Search size={20} />

            <div>
              <label>Search</label>

              <input
                type="text"
                placeholder="House, project or keyword"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchHouses();
                }}
              />
            </div>

          </div>

          <div className="search-field">

            <MapPin size={20} />

            <div>
              <label>Location</label>

              <input
                type="text"
                placeholder="City or area"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchHouses();
                }}
              />
            </div>

          </div>

          <div className="search-field price-field">

            <span className="rupee-symbol">₹</span>

            <div>
              <label>Min Price</label>

              <input
                type="number"
                placeholder="Any"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
            </div>

          </div>

          <div className="search-field price-field">

            <span className="rupee-symbol">₹</span>

            <div>
              <label>Max Price</label>

              <input
                type="number"
                placeholder="Any"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </div>

          </div>

          <button
            className="apply-filter-btn"
            onClick={() => fetchHouses()}
          >
            <SlidersHorizontal size={18} />
            Search
          </button>

        </div>

      </section>

      {/* =====================================
          QUICK FILTERS
      ===================================== */}

      <section className="quick-filter-section">

        <div className="quick-filters">

          <button
            className={activePrice === "all" ? "active" : ""}
            onClick={() => applyPriceChip("all")}
          >
            All
          </button>

          <button
            className={activePrice === "under30" ? "active" : ""}
            onClick={() => applyPriceChip("under30")}
          >
            Under ₹30L
          </button>

          <button
            className={activePrice === "30to60" ? "active" : ""}
            onClick={() => applyPriceChip("30to60")}
          >
            ₹30L – ₹60L
          </button>

          <button
            className={activePrice === "60to1cr" ? "active" : ""}
            onClick={() => applyPriceChip("60to1cr")}
          >
            ₹60L – ₹1Cr
          </button>

          <button
            className={activePrice === "1crplus" ? "active" : ""}
            onClick={() => applyPriceChip("1crplus")}
          >
            ₹1Cr+
          </button>

          <button
            className="more-filter-chip"
            onClick={() => fetchHouses()}
          >
            <SlidersHorizontal size={16} />
            More Filters
          </button>

        </div>

      </section>

      {/* =====================================
          RESULTS HEADER
      ===================================== */}

      <section className="buyhouse-results-header">

        <div>

          <p className="results-label">
            PROPERTY MARKETPLACE
          </p>

          <h2>
            {loading ? "Finding homes..." : `${houses.length} Houses Found`}
          </h2>

        </div>

        <div className="results-controls">

          <div className="view-toggle">

            <button
              className={viewMode === "grid" ? "active" : ""}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <Grid2X2 size={18} />
            </button>

            <button
              className={viewMode === "list" ? "active" : ""}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <List size={19} />
            </button>

          </div>

          <div className="sort-wrapper">

            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                fetchHouses({ sort: e.target.value });
              }}
            >
              <option value="latest">Latest First</option>
              <option value="priceLow">Price: Low → High</option>
              <option value="priceHigh">Price: High → Low</option>
            </select>

            <ChevronDown size={16} />

          </div>

        </div>

      </section>

      {/* =====================================
          PROPERTY LIST
      ===================================== */}

      <main
        className={
          viewMode === "grid"
            ? "buyhouse-list"
            : "buyhouse-list list-view"
        }
      >

        {loading ? (

          <div className="buyhouse-loading-state">

            <div className="loading-spinner" />

            <h3>Finding the right homes...</h3>

            <p>
              We're searching through available properties.
            </p>

          </div>

        ) : houses.length === 0 ? (

          <div className="buyhouse-empty-state">

            <div className="empty-icon">
              <Search size={30} />
            </div>

            <h3>No houses found</h3>

            <p>
              Try changing your location, price range or search terms.
            </p>

            <button onClick={resetFilters}>
              <RotateCcw size={16} />
              Reset Filters
            </button>

          </div>

        ) : (

          houses.map((h, index) => (

            <article
              key={h._id}
              className="buyhouse-card"
            >

              {/* IMAGE */}

              <div className="house-image-wrapper">

                {h.imageUrl ? (

                  <img
                    src={h.imageUrl}
                    alt={h.title || "House"}
                    className="buyhouse-image"
                    loading="lazy"
                  />

                ) : (

                  <div className="house-image-placeholder">
                    <span>🏠</span>
                    <p>Property Image</p>
                  </div>

                )}

                <div className="image-gradient" />

                <span
                  className={
                    index % 3 === 1
                      ? "property-badge featured"
                      : "property-badge"
                  }
                >
                  {index % 3 === 1 ? "Featured" : "New Listing"}
                </span>

                <button
                  className={`favorite-btn ${
                    favorites.includes(h._id) ? "liked" : ""
                  }`}
                  onClick={() => toggleFavorite(h._id)}
                  aria-label="Save property"
                >
                  <Heart
                    size={19}
                    fill={
                      favorites.includes(h._id)
                        ? "currentColor"
                        : "none"
                    }
                  />
                </button>

              </div>

              {/* CARD BODY */}

              <div className="buyhouse-info">

                <div className="house-title-row">

                  <div>

                    <h3>
                      {h.title || "Beautiful House"}
                    </h3>

                    <p className="house-location">
                      <MapPin size={15} />

                      {h.location?.city || "Location"},
                      {" "}
                      {h.location?.state || ""}
                    </p>

                  </div>

                  <ArrowUpRight
                    className="card-arrow"
                    size={20}
                  />

                </div>

                <div className="house-price-row">

                  <div>

                    <strong>
                      {formatPrice(h.price)}
                    </strong>

                    <span>
                      {formatFullPrice(h.price)}
                    </span>

                  </div>

                </div>

                <div className="seller-row">

                  <div className="seller-profile">

                    <div className="seller-avatar">

                      {h.postedBy?.photoURL ? (

                        <img
                          src={h.postedBy.photoURL}
                          alt={h.postedBy.name || "Seller"}
                        />

                      ) : (

                        <span>
                          {(h.postedBy?.name || "S")
                            .charAt(0)
                            .toUpperCase()}
                        </span>

                      )}

                    </div>

                    <div>

                      <strong>
                        {h.postedBy?.name || "Property Seller"}
                      </strong>

                      <span>
                        <BadgeCheck size={13} />
                        Verified Seller
                      </span>

                    </div>

                  </div>

                  <button
                    className="contact-btn"
                    onClick={() =>
                      contactSeller(h.postedBy)
                    }
                  >
                    Contact
                  </button>

                </div>

              </div>

            </article>

          ))

        )}

      </main>

      {/* =====================================
          TRUST STRIP
      ===================================== */}

      <section className="trust-strip">

        <div className="trust-item">

          <div className="trust-icon">
            <ShieldCheck size={22} />
          </div>

          <div>
            <strong>Verified Listings</strong>
            <span>Better property discovery</span>
          </div>

        </div>

        <div className="trust-item">

          <div className="trust-icon">
            <BadgeCheck size={22} />
          </div>

          <div>
            <strong>Trusted Sellers</strong>
            <span>Connect directly</span>
          </div>

        </div>

        <div className="trust-item">

          <div className="trust-icon">
            <MessageCircle size={22} />
          </div>

          <div>
            <strong>Easy Communication</strong>
            <span>Call, WhatsApp or Email</span>
          </div>

        </div>

        <div className="trust-item">

          <div className="trust-icon">
            <Users size={22} />
          </div>

          <div>
            <strong>Expert Support</strong>
            <span>We're here to help</span>
          </div>

        </div>

      </section>

      {/* =====================================
          CONTACT SHEET
      ===================================== */}

      {showConsult && (

        <div
          className="customer-sheet-overlay"
          onClick={() => setShowConsult(false)}
        >

          <div
            className="customer-sheet"
            onClick={(e) => e.stopPropagation()}
          >

            <div className="sheet-top">

              <div className="sheet-handle" />

              <button
                className="sheet-close"
                onClick={() => setShowConsult(false)}
              >
                <X size={20} />
              </button>

            </div>

            <p className="sheet-eyebrow">
              CIVILINK CONTACT
            </p>

            <h3 className="sheet-title">
              Contact Seller
            </h3>

            <p className="sheet-subtitle">
              Choose how you'd like to connect.
            </p>

            <div className="consult-profile">

              <div className="consult-avatar">

                {consultUser?.photoURL ? (

                  <img
                    src={consultUser.photoURL}
                    alt={consultUser.name || "Seller"}
                  />

                ) : (

                  <span>
                    {(consultUser?.name || "S")
                      .charAt(0)
                      .toUpperCase()}
                  </span>

                )}

              </div>

              <div>

                <h4>
                  {consultUser?.name || "Property Seller"}
                </h4>

                <p>
                  <BadgeCheck size={14} />
                  Verified Seller
                </p>

              </div>

            </div>

            <div className="contact-actions">

              {consultUser?.phone && (

                <>

                  <button
                    className="contact-action"
                    onClick={() => {
                      window.location.href =
                        `tel:${consultUser.phone}`;

                      setShowConsult(false);
                    }}
                  >

                    <div className="contact-action-icon phone">
                      <Phone size={20} />
                    </div>

                    <div>
                      <strong>Call Seller</strong>
                      <span>{consultUser.phone}</span>
                    </div>

                    <ArrowUpRight size={18} />

                  </button>

                  <button
                    className="contact-action"
                    onClick={() => {

                      const text = encodeURIComponent(
                        `Hello ${consultUser.name || ""},

I found your property on Civilink and would like to discuss it with you.`
                      );

                      const cleanPhone =
                        String(consultUser.phone).replace(
                          /\D/g,
                          ""
                        );

                      window.open(
                        `https://wa.me/91${cleanPhone}?text=${text}`,
                        "_blank"
                      );

                      setShowConsult(false);
                    }}
                  >

                    <div className="contact-action-icon whatsapp">
                      <MessageCircle size={20} />
                    </div>

                    <div>
                      <strong>WhatsApp</strong>
                      <span>Chat directly with seller</span>
                    </div>

                    <ArrowUpRight size={18} />

                  </button>

                </>

              )}

              {consultUser?.email && (

                <button
                  className="contact-action"
                  onClick={() => {

                    window.location.href =
                      `mailto:${consultUser.email}?subject=Property Inquiry from Civilink`;

                    setShowConsult(false);
                  }}
                >

                  <div className="contact-action-icon email">
                    <Mail size={20} />
                  </div>

                  <div>
                    <strong>Email Seller</strong>
                    <span>{consultUser.email}</span>
                  </div>

                  <ArrowUpRight size={18} />

                </button>

              )}

            </div>

            {!consultUser?.phone && !consultUser?.email && (

              <div className="contact-unavailable">
                Contact information is not available.
              </div>

            )}

            <button
              className="sheet-cancel-btn"
              onClick={() => setShowConsult(false)}
            >
              Cancel
            </button>

          </div>

        </div>

      )}

    </div>
  );
}

export default BuyHouse;